/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useReducer, useRef, useCallback, type ReactNode } from 'react'
import { uploadDocument } from '@/api/http/documents-api'
import { useToast } from '@/components/project/Toast'
import { uploadChunked } from './upload-chunked-api'

export type UploadStatus = 'queued' | 'uploading' | 'done' | 'error' | 'cancelled'

export interface UploadEntry {
  id: string
  file: File
  projectId: number
  folderId?: number
  title?: string
  progress: number
  status: UploadStatus
  speed: string
  error?: string
  dedup?: boolean
}

type UploadAction =
  | { type: 'ADD'; entries: UploadEntry[] }
  | { type: 'REMOVE'; id: string }
  | { type: 'SET_UPLOADING'; id: string }
  | { type: 'SET_PROGRESS'; id: string; progress: number; speed: string }
  | { type: 'SET_DONE'; id: string; dedup?: boolean }
  | { type: 'SET_ERROR'; id: string; error: string }
  | { type: 'SET_CANCELLED'; id: string }
  | { type: 'CLEAR_COMPLETED' }

interface UploadState {
  entries: UploadEntry[]
}

function uploadReducer(state: UploadState, action: UploadAction): UploadState {
  switch (action.type) {
    case 'ADD':
      return { entries: [...state.entries, ...action.entries] }
    case 'REMOVE':
      return { entries: state.entries.filter((e) => e.id !== action.id) }
    case 'SET_UPLOADING':
      return {
        entries: state.entries.map((e) =>
          e.id === action.id ? { ...e, status: 'uploading' as const, progress: 0 } : e
        ),
      }
    case 'SET_PROGRESS':
      return {
        entries: state.entries.map((e) =>
          e.id === action.id ? { ...e, progress: action.progress, speed: action.speed } : e
        ),
      }
    case 'SET_DONE':
      return {
        entries: state.entries.map((e) =>
          e.id === action.id ? { ...e, status: 'done' as const, progress: 100, dedup: action.dedup } : e
        ),
      }
    case 'SET_ERROR':
      return {
        entries: state.entries.map((e) =>
          e.id === action.id ? { ...e, status: 'error' as const, error: action.error } : e
        ),
      }
    case 'SET_CANCELLED':
      return {
        entries: state.entries.map((e) =>
          e.id === action.id ? { ...e, status: 'cancelled' as const } : e
        ),
      }
    case 'CLEAR_COMPLETED':
      return {
        entries: state.entries.filter((e) => e.status === 'queued' || e.status === 'uploading'),
      }
  }
}

interface UploadContextValue {
  entries: UploadEntry[]
  addUpload: (entries: UploadEntry[]) => void
  cancelUpload: (id: string) => void
  retryUpload: (id: string) => void
  clearCompleted: () => void
  activeCount: number
  completedCount: number
}

const UploadContext = createContext<UploadContextValue | null>(null)

const CHUNKED_THRESHOLD = 100 * 1024 * 1024 // 100MB

export function UploadProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(uploadReducer, { entries: [] })
  const { toast } = useToast()
  const xhrMap = useRef<Map<string, XMLHttpRequest | AbortController>>(new Map())

  const executeUpload = useCallback(async (entry: UploadEntry) => {
    dispatch({ type: 'SET_UPLOADING', id: entry.id })

    try {
      if (entry.file.size >= CHUNKED_THRESHOLD) {
        // Chunked upload for large files
        const ctrl = new AbortController()
        xhrMap.current.set(entry.id, ctrl)

        await uploadChunked(entry.projectId, entry.file, {
          folderId: entry.folderId,
          onProgress: (pct, speed) => {
            dispatch({ type: 'SET_PROGRESS', id: entry.id, progress: pct, speed })
          },
          signal: ctrl.signal,
        })

        dispatch({ type: 'SET_DONE', id: entry.id })
        toast('info', `«${entry.file.name}» — загружен`)
      } else {
        // Multipart upload for small files
        const speedData = new Map<string, { time: number; loaded: number }>()
        const result = await new Promise<{ id: number; deduplicated?: boolean }>((resolve, reject) => {
          uploadDocument(
            entry.projectId,
            entry.file,
            entry.folderId,
            entry.title,
            (pct) => {
              let speed = ''
              const now = Date.now()
              const prev = speedData.get(entry.id)
              if (prev) {
                const elapsed = (now - prev.time) / 1000
                if (elapsed > 0.3) {
                  const loaded = (pct / 100) * entry.file.size
                  const bps = (loaded - prev.loaded) / elapsed
                  speed = bps >= 1024 * 1024
                    ? `${(bps / 1024 / 1024).toFixed(1)} МБ/с`
                    : `${(bps / 1024).toFixed(0)} КБ/с`
                }
              }
              speedData.set(entry.id, { time: now, loaded: (pct / 100) * entry.file.size })
              dispatch({ type: 'SET_PROGRESS', id: entry.id, progress: pct, speed })
            },
            (xhr) => {
              xhrMap.current.set(entry.id, xhr)
            },
          ).then(resolve, reject)
        })

        dispatch({ type: 'SET_DONE', id: entry.id, dedup: result.deduplicated })
        if (result.deduplicated) {
          toast('info', `«${entry.file.name}» — файл уже был, создана ссылка`)
        } else {
          toast('info', `«${entry.file.name}» — загружен`)
        }
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Ошибка'
      if (msg === 'aborted' || (err instanceof DOMException && err.name === 'AbortError')) {
        dispatch({ type: 'SET_CANCELLED', id: entry.id })
      } else if (msg.includes('уже есть в проекте')) {
        dispatch({ type: 'SET_DONE', id: entry.id, dedup: true })
        toast('info', `«${entry.file.name}» — файл уже есть в проекте`)
      } else {
        dispatch({ type: 'SET_ERROR', id: entry.id, error: msg })
        toast('error', `«${entry.file.name}»: ${msg}`)
      }
    } finally {
      xhrMap.current.delete(entry.id)
    }
  }, [toast])

  const addUpload = useCallback((entries: UploadEntry[]) => {
    dispatch({ type: 'ADD', entries })
    for (const entry of entries) {
      executeUpload(entry)
    }
  }, [executeUpload])

  const cancelUpload = useCallback((id: string) => {
    const entry = state.entries.find((e) => e.id === id)
    if (!entry || entry.status === 'done' || entry.status === 'cancelled') return

    const xhr = xhrMap.current.get(id)
    if (xhr instanceof XMLHttpRequest) {
      xhr.abort()
    } else if (xhr instanceof AbortController) {
      xhr.abort()
    }
    dispatch({ type: 'SET_CANCELLED', id })
    xhrMap.current.delete(id)
  }, [state.entries])

  const retryUpload = useCallback((id: string) => {
    const entry = state.entries.find((e) => e.id === id)
    if (entry) executeUpload(entry)
  }, [state.entries, executeUpload])

  const clearCompleted = useCallback(() => {
    dispatch({ type: 'CLEAR_COMPLETED' })
  }, [])

  const activeCount = state.entries.filter((e) => e.status === 'queued' || e.status === 'uploading').length
  const completedCount = state.entries.filter((e) => e.status === 'done').length

  return (
    <UploadContext.Provider value={{
      entries: state.entries,
      addUpload,
      cancelUpload,
      retryUpload,
      clearCompleted,
      activeCount,
      completedCount,
    }}>
      {children}
    </UploadContext.Provider>
  )
}

export function useUpload(): UploadContextValue {
  const ctx = useContext(UploadContext)
  if (!ctx) throw new Error('useUpload must be used within UploadProvider')
  return ctx
}
